import { useAuthContext } from '@authentication/AuthContext'
import {
	AdminSuggestion,
	filterMentionedAdmins,
	filterMentionedSlackUsers,
	parseAdminSuggestions,
} from '@components/Comment/utils/utils'
import { toast } from '@components/Toaster'
import {
	useCreateErrorCommentMutation,
	useCreateSessionCommentMutation,
	useGetCommentMentionSuggestionsQuery,
	useGetWorkspaceAdminsByProjectIdQuery,
} from '@graph/hooks'
import { namedOperations } from '@graph/operations'
import {
	Admin,
	SanitizedAdminInput,
	SanitizedSlackChannelInput,
	Session,
} from '@graph/schemas'
import {
	Box,
	ButtonIcon,
	Form,
	IconSolidPaperAirplane,
	Stack,
} from '@highlight-run/ui/components'
import { CommentTextBody } from '@pages/Player/Toolbar/NewCommentForm/CommentTextBody/CommentTextBody'
import analytics from '@util/analytics'
import { getCommentMentionSuggestions } from '@util/comment/util'
import { useParams } from '@util/react-router/useParams'
import React, { useEffect, useMemo, useState } from 'react'
import { OnChangeHandlerFunc } from 'react-mentions'

import { CommentMentionButton } from '@/components/Comment/CommentMentionButton'

import { Coordinates2D } from '../../PlayerCommentCanvas/PlayerCommentCanvas'
import usePlayerConfiguration from '../../PlayerHook/utils/usePlayerConfiguration'

interface Props {
	commentTime: number
	onCloseHandler: () => void
	commentPosition: Coordinates2D | undefined
	session?: Session
	session_secure_id?: string
	error_secure_id?: string
	/** Passed from NewCommentModal; reserved for future header copy. */
	modalHeader?: string
	/** Passed from NewCommentModal for error-context comments. */
	errorTitle?: string
	currentUrl?: string
}

export const NewCommentForm = ({
	commentTime,
	onCloseHandler,
	commentPosition,
	session_secure_id,
	error_secure_id,
	modalHeader: _modalHeader,
	errorTitle: _errorTitle,
	currentUrl,
}: Props) => {
	const [createComment] = useCreateSessionCommentMutation({
		refetchQueries: [
			namedOperations.Query.GetSessionComments,
			namedOperations.Query.GetSessions,
		],
	})
	const [createErrorComment] = useCreateErrorCommentMutation()
	const { admin, isLoggedIn } = useAuthContext()
	const { project_id } = useParams<{ project_id: string }>()
	const [commentText, setCommentText] = useState('')
	const inputRef = React.useRef<HTMLTextAreaElement>(null)
	const [commentTextForEmail, setCommentTextForEmail] = useState('')
	const [isCreatingComment, setIsCreatingComment] = useState(false)
	const formStore = Form.useStore({
		defaultValues: {
			commentText: '',
			issueTitle: '',
			issueDescription: '',
		},
	})
	const {
		selectedTimelineAnnotationTypes,
		setSelectedTimelineAnnotationTypes,
	} = usePlayerConfiguration()
	const { data: adminsInWorkspace } = useGetWorkspaceAdminsByProjectIdQuery({
		variables: { project_id: project_id! },
		skip: !project_id,
	})
	const { data: mentionSuggestionsData } =
		useGetCommentMentionSuggestionsQuery({
			variables: { project_id: project_id! },
			skip: !project_id,
		})
	const [mentionedAdmins, setMentionedAdmins] = useState<
		SanitizedAdminInput[]
	>([])
	const [mentionedSlackUsers, setMentionedSlackUsers] = useState<
		SanitizedSlackChannelInput[]
	>([])

	const onCreateErrorComment = async () => {
		analytics.track('Create Error Comment', {
			numHighlightAdminMentions: mentionedAdmins.length,
			numSlackMentions: mentionedSlackUsers.length,
		})
		setIsCreatingComment(true)

		try {
			await createErrorComment({
				variables: {
					project_id: project_id!,
					error_group_secure_id: error_secure_id || '',
					text: commentText.trim(),
					text_for_email: commentTextForEmail.trim(),
					error_url: `${window.location.origin}${window.location.pathname}`,
					tagged_admins: mentionedAdmins,
					tagged_slack_users: mentionedSlackUsers,
					author_name: admin?.name || admin?.email || 'Someone',
					integrations: [],
					issue_title: null,
					issue_team_id: undefined,
					issue_description: null,
					issue_type_id: undefined,
				},
				refetchQueries: [namedOperations.Query.GetErrorComments],
			})
			formStore.reset()
			setCommentText('')
			onCloseHandler()
		} catch (_e) {
			const e = _e as Error
			analytics.track('Create Error Comment Failed', {
				error: e.toString(),
			})
			toast.error('Failed to post a comment, please try again.')
		}
		setIsCreatingComment(false)
	}

	const onCreateSessionComment = async () => {
		analytics.track('Create Comment', {
			numHighlightAdminMentions: mentionedAdmins.length,
			numSlackMentions: mentionedSlackUsers.length,
		})
		setIsCreatingComment(true)

		try {
			await createComment({
				variables: {
					project_id: project_id!,
					session_secure_id: session_secure_id || '',
					session_timestamp: Math.floor(commentTime),
					text: commentText.trim(),
					text_for_email: commentTextForEmail.trim(),
					x_coordinate: commentPosition?.x || 0,
					y_coordinate: commentPosition?.y || 0,
					session_url: `${window.location.origin}${window.location.pathname}`,
					tagged_admins: mentionedAdmins,
					tagged_slack_users: mentionedSlackUsers,
					tags: [],
					time: commentTime / 1000,
					author_name: admin?.name || admin?.email || 'Someone',
					issue_team_id: undefined,
					integrations: [],
					issue_title: null,
					issue_description: null,
					additional_context: currentUrl
						? `*User\'s URL* <${currentUrl}|${currentUrl}>`
						: null,
					issue_type_id: undefined,
				},
				refetchQueries: [namedOperations.Query.GetSessionComments],
			})
			onCloseHandler()
			formStore.reset()
			if (!selectedTimelineAnnotationTypes.includes('Comments')) {
				setSelectedTimelineAnnotationTypes([
					...selectedTimelineAnnotationTypes,
					'Comments',
				])
			}
		} catch (_e) {
			const e = _e as Error

			analytics.track('Create Comment Failed', { error: e.toString() })
			toast.error(
				'Failed to post a comment, please try again. If this keeps failing please reach out to us!',
			)
		}
		setIsCreatingComment(false)
	}

	const handleSubmit = () => {
		if (session_secure_id?.length) {
			return onCreateSessionComment()
		} else if (error_secure_id?.length) {
			return onCreateErrorComment()
		} else {
			throw new Error('comment form should have session or error id set')
		}
	}

	const adminSuggestions: AdminSuggestion[] = useMemo(
		() =>
			isLoggedIn
				? parseAdminSuggestions(
						getCommentMentionSuggestions(mentionSuggestionsData),
						admin,
						mentionedAdmins,
					)
				: [],
		[admin, isLoggedIn, mentionSuggestionsData, mentionedAdmins],
	)

	const onDisplayTransform = (_id: string, display: string): string => {
		return display
	}

	const onChangeHandler: OnChangeHandlerFunc = (
		e,
		_newValue,
		newPlainTextValue,
		mentions,
	) => {
		setCommentTextForEmail(newPlainTextValue)

		if (adminsInWorkspace?.admins) {
			setMentionedAdmins(
				filterMentionedAdmins(
					adminsInWorkspace.admins.map((wa) => wa.admin),
					mentions,
				),
			)
		}

		if (mentionSuggestionsData?.slack_channel_suggestion) {
			setMentionedSlackUsers(
				filterMentionedSlackUsers(
					mentionSuggestionsData.slack_channel_suggestion,
					mentions,
				),
			)
		}
		setCommentText(e.target.value)
	}

	const onFormChangeHandler: React.KeyboardEventHandler<HTMLFormElement> = (
		e,
	) => {
		if (e.key === 'Enter' && e.metaKey) {
			handleSubmit()
		}
	}

	const placeholder = useMemo(
		() => getNewCommentPlaceholderText(adminSuggestions, admin),
		[admin, adminSuggestions],
	)

	useEffect(() => {
		inputRef.current?.focus()
	}, [])

	return (
		<Box>
			<Form
				name="newComment"
				onSubmit={handleSubmit}
				store={formStore}
				onKeyDown={onFormChangeHandler}
			>
				<Box p="6">
					<Box>
						<CommentTextBody
							newInput
							inputRef={inputRef}
							commentText={commentText}
							onChangeHandler={onChangeHandler}
							placeholder={placeholder}
							suggestions={adminSuggestions}
							onDisplayTransformHandler={onDisplayTransform}
						/>
					</Box>
					<Stack
						my="2"
						direction="row"
						justify="space-between"
						align="center"
					>
						<CommentMentionButton
							commentText={commentText}
							inputRef={inputRef}
							setCommentText={setCommentText}
						/>
						<ButtonIcon
							kind="primary"
							emphasis="high"
							size="xSmall"
							disabled={
								isCreatingComment || commentText.length === 0
							}
							onClick={handleSubmit}
							icon={<IconSolidPaperAirplane />}
						/>
					</Stack>
				</Box>
			</Form>
		</Box>
	)
}

const RANDOM_COMMENT_MESSAGES = [
	'check this out!',
	'what do you think of this?',
	'should we update this?',
	'looks like the user was having trouble here.',
] as const

const getNewCommentPlaceholderText = (
	adminSuggestions?: AdminSuggestion[],
	admin?: Admin,
) => {
	const randomMessage =
		RANDOM_COMMENT_MESSAGES[
			Math.floor(Math.random() * RANDOM_COMMENT_MESSAGES.length)
		]

	if (!adminSuggestions || !admin) {
		return randomMessage
	}
	if (adminSuggestions.length === 0) {
		return `Hey @${admin.name}, ${randomMessage}`
	}

	const randomSuggestionIndex = Math.floor(
		Math.random() * adminSuggestions.length,
	)
	let displayName = adminSuggestions[randomSuggestionIndex].display || ''

	if (!(displayName[0] === '@') && !(displayName[0] === '#')) {
		displayName = `@${displayName}`
	} else if (displayName.includes('#')) {
		displayName = `@${displayName.slice(1)}`
	}

	return `Hey ${displayName}, ${randomMessage}`
}
